import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { INSIGHTS } from "@/data/insights";
import { AmbientLight } from "@/components/brand/AmbientLight";
import ChapterMark from "@/components/brand/ChapterMark";
import { ConcentricMonogram } from "@/components/brand/ConcentricMonogram";

export default function ResourceCenterPreview() {
  const [featured, ...rest] = INSIGHTS;
  return (
    <section
      data-testid="resources-section"
      className="relative py-24 sm:py-32 lg:py-52 overflow-hidden bg-midnight-gradient"
    >
      <AmbientLight size={1000} color="blue" intensity={0.32} style={{ top: "20%", left: "-15%" }} />
      <AmbientLight size={620} color="gold" intensity={0.15} style={{ bottom: "5%", right: "5%" }} />

      <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12">
        <ChapterMark index="11" label="Policyholder Resource Center" className="mb-14" />

        <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-20 lg:mb-24 gap-10">
          <div className="max-w-2xl">
            <h2 className="font-display-tight text-white text-[clamp(2.5rem,5.2vw,4.75rem)] leading-[0.96] tracking-[-0.045em]">
              Educational authority,
              <br />
              <span className="gold-metallic font-display font-light">for the policyholder.</span>
            </h2>
            <div className="mt-10 h-px w-16 bg-gradient-to-r from-gold to-transparent" />
          </div>
          <Link
            to="/insights"
            data-testid="resources-view-all"
            className="inline-flex items-center gap-3 font-mono text-xs tracking-[0.22em] uppercase text-white/70 hover:text-gold transition-colors group self-start lg:self-end"
          >
            <span className="h-px w-8 bg-gold/40 group-hover:bg-gold transition-colors" />
            Browse Resources
            <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </Link>
        </div>

        <div className="grid lg:grid-cols-12 gap-4 lg:gap-5">
          {/* Featured article — editorial spread */}
          <motion.article
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="lg:col-span-7 glass-strong p-10 lg:p-16 group hover:border-gold/30 transition-all duration-500 relative overflow-hidden"
            data-testid={`insight-featured-${featured.slug}`}
            style={{
              boxShadow:
                "inset 0 1px 0 rgba(198,168,107,0.15), 0 50px 100px -30px rgba(0,119,255,0.25)",
            }}
          >
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold to-transparent" />
            <div
              className="pointer-events-none absolute -right-24 -bottom-24 opacity-30 group-hover:opacity-50 transition-opacity duration-700"
              aria-hidden
            >
              <ConcentricMonogram size={420} variant="ghost" glowColor="blue" rotateRings />
            </div>
            <div className="relative">
              <div className="flex items-center gap-3 mb-8">
                <span className="font-mono text-[0.62rem] text-gold tracking-[0.32em] uppercase">{featured.category}</span>
                <span className="w-8 h-px bg-gold/40" />
                <span className="font-mono text-[0.62rem] text-ag-text-muted tracking-[0.28em] uppercase">Featured</span>
              </div>
              <h3 className="font-display-tight text-white text-[clamp(2rem,3.6vw,3.5rem)] leading-[1.02] tracking-[-0.04em] mb-8 group-hover:gold-metallic group-hover:text-transparent transition-colors font-light max-w-2xl">
                {featured.title}
              </h3>
              <p className="text-ag-text-secondary text-base lg:text-[1.08rem] leading-[1.75] font-light mb-10 max-w-xl">
                {featured.excerpt}
              </p>
              <div className="flex items-center gap-4 font-mono text-[0.62rem] text-ag-text-muted tracking-[0.28em] uppercase">
                <span>{featured.read_time}</span>
                <span className="w-6 h-px bg-white/15" />
                <span className="text-gold">Foundational</span>
              </div>
            </div>
          </motion.article>

          <div className="lg:col-span-5 space-y-4 lg:space-y-5">
            {rest.map((article, i) => (
              <motion.div
                key={article.slug}
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="glass p-8 lg:p-9 hover:border-gold/30 transition-all duration-500 group cursor-pointer"
                data-testid={`insight-${article.slug}`}
              >
                <div className="flex items-center gap-3 mb-4">
                  <span className="font-mono text-[0.58rem] text-gold tracking-[0.32em] uppercase">
                    {article.category}
                  </span>
                  <div className="h-px flex-1 bg-gold/15" />
                  <span className="font-mono text-[0.55rem] text-ag-text-muted tracking-[0.24em] uppercase">
                    {String(i + 2).padStart(2, "0")}
                  </span>
                </div>
                <h4 className="font-display text-lg lg:text-xl text-white group-hover:gold-metallic group-hover:text-transparent transition-colors leading-[1.3] mb-3 font-light tracking-[-0.025em]">
                  {article.title}
                </h4>
                <p className="text-[0.85rem] text-ag-text-secondary leading-[1.7] font-light line-clamp-2">
                  {article.excerpt}
                </p>
                <div className="mt-5 font-mono text-[0.58rem] text-ag-text-muted tracking-[0.26em] uppercase">
                  {article.read_time}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
